import { faTasks, faThumbsUp, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { ActionButton, CatchWeightCard, CatchWeightQuantityCard, POCardLists } from 'components';
import { ConfirmationPopup, SecondaryPopup } from 'components/pop-up';
import { TripContext } from 'contexts';
import { CatchWeightOptions, SecondaryPopUpField } from 'enums';
import React, { FC, Fragment, useContext, useState } from 'react';
import { useOrderItemService, useReturnItemService } from 'services';
import { ReasonClass, ReconcileReasonType } from 'services/enums';
import { CatchWeight, LicensePlate, Order, OrderItem, OrderItemChange, ReconcileReason, RefusalReason } from 'services/types';
import { SecondaryPopUpValue } from 'types';
import { Switch } from 'devextreme-react';


type Props = {
    stopId: number | undefined
    lineItemId: number
    onReconcilingStarted?: () => void
    onReconcilingCompleted?: () => void
    onCatchWeightUnverified: () => void
}


export const VerifyItemCatchWeight: FC<Props> = ({ stopId, lineItemId, onReconcilingStarted, onReconcilingCompleted, onCatchWeightUnverified}) => {

    const tripContext = useContext(TripContext)

    const { updateCatchWeightItem } = useOrderItemService()
    const { isValidReturnSticker,isReturnStickerDuplicate } = useReturnItemService()

    const [reconcileCatchWeight, setReconcileCatchWeight] = useState<SecondaryPopUpValue>({} as SecondaryPopUpValue)
    const [isShowVerifiedItemsEnabled, setIsShowVerifiedItemsEnabled] = useState<boolean>(true)
    const [isSecondaryPopupAddVisible, setSecondaryPopupAddVisible] = useState<boolean>(false)
    const [isSecondaryPopupDeleteVisible, setSecondaryPopupDeleteVisible] = useState<boolean>(false)
    const [isReturnStickerAvailable, setIsReturnStickerAvailable] = useState<boolean>(true)
    const [isUndoClicked, setIsUndoClicked] = useState<boolean>(false)
    const [isVerifyAllClicked, setIsVerifyAllClicked] = useState<boolean>(false)
    const [isDeleteAllClicked, setIsDeleteAllClicked] = useState<boolean>(false)
    const [selectedCatchWeightId, setSelectedCatchWeightId] = useState<number>(0)
    const [isToggleClicked, setToggleClicked] = useState<boolean>(false)
    const [isShowCombinePOS, setIsShowCombinePOS] = useState<boolean>(true)

    const refusalReasons: RefusalReason[] = tripContext.refusalReasons.toArray() ?? []
    const reconcileReasons: ReconcileReason[] = tripContext.reconcileReasons.toArray() ?? []
    let items: OrderItem[] = []
    let catchWeightItems: CatchWeight[] = []
    let changes: OrderItemChange[] = []

    const orders: Order[] = tripContext.orders.filter(o => o.stopId == stopId)
                     
    orders.forEach(order => {
        items = [...items, ...tripContext.orderItems.filter(oi => oi.orderId == order.id && oi.lineItemId == lineItemId)]
    })

    items.forEach(orderItem => {
        changes = [...changes, ...tripContext.orderItemChanges.filter(cw => cw.orderItemId == orderItem.id)]
        catchWeightItems = [...catchWeightItems, ...tripContext.catchWeights.filter(cw => cw.orderItemId == orderItem.id)]
    })

    const item: OrderItem = items.length > 0 ? items[0] : {} as OrderItem

    const startReconcilingCatchWeight = (type: CatchWeightOptions.Add | CatchWeightOptions.Delete): void => {
        if(type === CatchWeightOptions.Add) {
            setSecondaryPopupAddVisible(true)
        }
        else if(type === CatchWeightOptions.Delete) {
            setSecondaryPopupDeleteVisible(true)
        }
        onReconcilingStarted != null && onReconcilingStarted()
    }

    const completeReconcilingCatchWeight = (type: CatchWeightOptions.Add | CatchWeightOptions.Delete): void => { 
        if(type === CatchWeightOptions.Add) {
            setSecondaryPopupAddVisible(false)
        }
        else if(type === CatchWeightOptions.Delete) {
            setSecondaryPopupDeleteVisible(false)
        }
        onReconcilingCompleted != null && onReconcilingCompleted()
    }

    const onAddCatchWeight = (orderId?: number): void => {

        let newCatchWeightItem: SecondaryPopUpValue = {
            id: new Date().getTime(),
            reconcileReasonId: null,
            refusalChoiceId: null,
            returnSticker: null,
            noReturnSticker: null,
            comments: null,
            codeDate: null,
            enableButton: false,
            isReturnStickerDuplicate: null,
            orderId: orderId
        }
        setIsReturnStickerAvailable(true)
        setReconcileCatchWeight(newCatchWeightItem)
        startReconcilingCatchWeight(CatchWeightOptions.Add)
    }

    const undoOperation = (): void => {

        let index: number = catchWeightItems.findIndex(item => item.id === selectedCatchWeightId)
        if (catchWeightItems[index].catchWeightOptions == CatchWeightOptions.Add) {
            updateCatchWeightItem(catchWeightItems[index], CatchWeightOptions.Undo)
            catchWeightItems.splice(index, 1);
        }
        else {
            catchWeightItems[index].adjustedWeight = null
            catchWeightItems[index].isVerified = false
            catchWeightItems[index].reconcileReasonId = null
            catchWeightItems[index].returnSticker = null
            catchWeightItems[index].noReturnStickerReason = null
            catchWeightItems[index].refusalChoiceId = null
            catchWeightItems[index].refusalComment = null
            catchWeightItems[index].codeDate = null
            catchWeightItems[index].catchWeightOptions = CatchWeightOptions.NotVerified

            onCatchWeightUnverified()
        }
        reloadCatchWeightItem(catchWeightItems)
        setIsUndoClicked(false)
    }

    const onClickOperationMode = (options: CatchWeightOptions, id: number, editedWeight: number): void => {
        let index: number = catchWeightItems.findIndex(item => item.id === id)
        setSelectedCatchWeightId(id)
        switch (options) {
            case CatchWeightOptions.Add:
                catchWeightItems[index].adjustedWeight = editedWeight
                catchWeightItems[index].catchWeightOptions = options
                break;
            case CatchWeightOptions.Edit:
                catchWeightItems[index].isVerified = true
                catchWeightItems[index].adjustedWeight = editedWeight
                catchWeightItems[index].catchWeightOptions = options
                catchWeightItems[index].reconcileReasonId = ReconcileReasonType.EditedCatchWeight
                break;
            case CatchWeightOptions.Delete:
                let reconcileCatchWeightItem: SecondaryPopUpValue = {
                    id: catchWeightItems[index].id,
                    reconcileReasonId: null,
                    refusalChoiceId: null,
                    returnSticker: null,
                    noReturnSticker: null,
                    comments: null,
                    codeDate: null,
                    enableButton: false,
                    isReturnStickerDuplicate: null,
                    orderId: null
                }
                setReconcileCatchWeight(reconcileCatchWeightItem)
                startReconcilingCatchWeight(CatchWeightOptions.Delete)
                break;
            case CatchWeightOptions.Undo:
                setIsUndoClicked(true)
                break;
            case CatchWeightOptions.Verified:
                catchWeightItems[index].isVerified = true
                catchWeightItems[index].adjustedWeight = editedWeight
                catchWeightItems[index].catchWeightOptions = options
                catchWeightItems[index].reconcileReasonId = null
                catchWeightItems[index].returnSticker = null
                catchWeightItems[index].noReturnStickerReason = null
                catchWeightItems[index].refusalChoiceId = null
                catchWeightItems[index].refusalComment = null
                break;
        }
        reloadCatchWeightItem(catchWeightItems)
    }

    const reloadCatchWeightItem = (items: CatchWeight[]): void => {
        tripContext.catchWeights.bulkPut(items)
        let verifiedCatchWeightItems: CatchWeight[] = items.filter(verifiedCatchWeightItems => verifiedCatchWeightItems.isVerified && (verifiedCatchWeightItems.reconcileReasonId == null || reconcileReasons.some(reason => reason.id == verifiedCatchWeightItems.reconcileReasonId && reason.class != ReasonClass.Shortage)))
        let catchWeightOrderItems: OrderItem[] = tripContext.orderItems.filter(oi => verifiedCatchWeightItems.some(cw => cw.orderItemId == oi.id))

        catchWeightOrderItems.forEach(oi => {
            let catchWeights = items.filter(cw => cw.orderItemId == oi.id)
            tripContext.orderItems.put({...oi, countedQuantity: catchWeights.length})
        })
    }
    
    function toggleButtonClick ( ) {
        setToggleClicked(!isToggleClicked)
    }

    const updateCatchWeights = (options: CatchWeightOptions): void => {
        let items: CatchWeight[] = [...catchWeightItems]
        for (let i = 0; i < items.length; i++) {
            let item = items[i]
            
            if (isToggleClicked === true) {
                if (item.catchWeightOptions === CatchWeightOptions.Verified || item.catchWeightOptions == null || item.catchWeightOptions === CatchWeightOptions.NotVerified)
                {
                    setReconcileCatchWeight({} as SecondaryPopUpValue)
                    startReconcilingCatchWeight(CatchWeightOptions.Delete)
                }
            }
            else {
                if (item.catchWeightOptions == null || item.catchWeightOptions === CatchWeightOptions.NotVerified) {
                    if (options == CatchWeightOptions.Verified)
                    {
                        items[i] = {
                            ...item,
                            catchWeightOptions: options,
                            isVerified: true,
                            adjustedWeight: item.shippedWeight
                        }
                    }
                    else if (options == CatchWeightOptions.Delete) {
                        setReconcileCatchWeight({} as SecondaryPopUpValue)
                        startReconcilingCatchWeight(CatchWeightOptions.Delete)
                    }
                }
            }
        } 
        reloadCatchWeightItem(items)
        setIsVerifyAllClicked(false)
        setIsDeleteAllClicked(false)
    }

    let verifyButtonText = changes && changes.length > 0 ? "Verify Rest" : "Verify All"
    let deleteButtonText = changes && changes.length > 0 ? "Delete Rest" : "Delete All"

    const onClickSecondaryPopup = async (): Promise<void> => {

        if (isSecondaryPopupAddVisible) {
            const refCatchWeight = catchWeightItems[0]
            let newCatchWeightItem: CatchWeight = {
                id: new Date().getTime(),
                aS400StopId: refCatchWeight.aS400StopId,
                sequenceNumber: refCatchWeight.sequenceNumber,
                relativeRecordNumber: 0,
                refSequence: refCatchWeight.refSequence,
                orderItemId: reconcileCatchWeight.orderId || 0,
                shippedWeight: 0,
                adjustedWeight: reconcileCatchWeight.addCatchWeight! > 0 ? reconcileCatchWeight.addCatchWeight! : 1,
                lotNumber: null,
                isVerified: true,
                returnSticker: reconcileCatchWeight.returnSticker,
                noReturnStickerReason: reconcileCatchWeight.noReturnSticker,
                reconcileReasonId: reconcileCatchWeight.reconcileReasonId,
                refusalChoiceId: reconcileCatchWeight.refusalChoiceId,
                refusalComment: reconcileCatchWeight.comments,
                codeDate: reconcileCatchWeight.codeDate,
                catchWeightOptions: CatchWeightOptions.Add
            }
            
            let catchWeights: CatchWeight[] = [newCatchWeightItem, ...catchWeightItems]
            reloadCatchWeightItem(catchWeights)
            updateCatchWeightItem(newCatchWeightItem, CatchWeightOptions.Add)
            completeReconcilingCatchWeight(CatchWeightOptions.Add)
        }
        else if (isSecondaryPopupDeleteVisible) {

            if (reconcileCatchWeight.id == undefined || reconcileCatchWeight.id == null) //Delete All
            {
                let items: CatchWeight[] = [...catchWeightItems]
                for (let i = 0; i < items.length; i++) {
                    let item = items[i]
                    if (isToggleClicked == true || item.catchWeightOptions == undefined || item.catchWeightOptions === CatchWeightOptions.NotVerified) {
                        items[i] = {
                            ...item,
                            catchWeightOptions: CatchWeightOptions.Delete,
                            isVerified: true,
                            adjustedWeight: 0,
                            reconcileReasonId: reconcileCatchWeight.reconcileReasonId,
                            refusalChoiceId: reconcileCatchWeight.refusalChoiceId,
                            returnSticker: reconcileCatchWeight.returnSticker,
                            noReturnStickerReason: reconcileCatchWeight.noReturnSticker,
                            refusalComment: reconcileCatchWeight.comments,
                            codeDate: reconcileCatchWeight.codeDate
                        }
                    }
                }
                reloadCatchWeightItem(items)
            }
            else {
                let index: number = catchWeightItems.findIndex(item => item.id === reconcileCatchWeight.id)
                catchWeightItems[index].isVerified = true
                catchWeightItems[index].catchWeightOptions = CatchWeightOptions.Delete
                catchWeightItems[index].adjustedWeight = 0
                catchWeightItems[index].returnSticker = reconcileCatchWeight.returnSticker
                catchWeightItems[index].noReturnStickerReason = reconcileCatchWeight.noReturnSticker
                catchWeightItems[index].reconcileReasonId = reconcileCatchWeight.reconcileReasonId
                catchWeightItems[index].refusalChoiceId = reconcileCatchWeight.refusalChoiceId
                catchWeightItems[index].refusalComment = reconcileCatchWeight.comments
                catchWeightItems[index].codeDate = reconcileCatchWeight.codeDate
                reloadCatchWeightItem(catchWeightItems)
            }
            completeReconcilingCatchWeight(CatchWeightOptions.Delete)
        }
    }

    const onValueChanged = async (secondaryPopUpField: SecondaryPopUpField, value: any): Promise<void> => {

        let newOrderItemChange: SecondaryPopUpValue = reconcileCatchWeight
        switch (secondaryPopUpField) {
            case SecondaryPopUpField.AddCatchWeight:
                newOrderItemChange = { ...newOrderItemChange!, addCatchWeight: value && value > 0 ? value : 1 }
                break;
            case SecondaryPopUpField.ReconcileReasonId:
                newOrderItemChange = { ...newOrderItemChange!, reconcileReasonId: value }
                break;
            case SecondaryPopUpField.RefusalChoiceId:
                newOrderItemChange = { ...newOrderItemChange!, refusalChoiceId: value }
                break;
            case SecondaryPopUpField.ReturnSticker:
                newOrderItemChange = { ...newOrderItemChange!, returnSticker: value == 0 ? null : +value, noReturnSticker: null }
                setIsReturnStickerAvailable(true)
                break;
            case SecondaryPopUpField.NoReturnSticker:
                newOrderItemChange = { ...newOrderItemChange!, noReturnSticker: value == "" ? null : value, returnSticker: null }
                setIsReturnStickerAvailable(false)
                break;
            case SecondaryPopUpField.Comments:
                newOrderItemChange = { ...newOrderItemChange!, comments: value }
                break;
            case SecondaryPopUpField.CodeDate:
                newOrderItemChange = { ...newOrderItemChange!, codeDate: value }
                break;
        }

        newOrderItemChange.isReturnStickerDuplicate = isReturnStickerDuplicate(newOrderItemChange.returnSticker, null, null, null, reconcileCatchWeight.id);

        const isValid = isValidReturnSticker(newOrderItemChange.returnSticker) || newOrderItemChange.noReturnSticker != null
        if ((newOrderItemChange.addCatchWeight != null && newOrderItemChange.reconcileReasonId == ReconcileReasonType.OverageKept) ||
            newOrderItemChange.reconcileReasonId === ReconcileReasonType.Destroyed ||
            newOrderItemChange.reconcileReasonId === ReconcileReasonType.ReturnHold ||
            newOrderItemChange.reconcileReasonId === ReconcileReasonType.Shortage)
            newOrderItemChange = { ...newOrderItemChange!, enableButton: true }
        else if (newOrderItemChange.refusalChoiceId != null && (newOrderItemChange.returnSticker != null || newOrderItemChange.noReturnSticker != null))
            newOrderItemChange = {...newOrderItemChange!, enableButton: isValid }
        else
            newOrderItemChange = { ...newOrderItemChange!, enableButton: false }

        setReconcileCatchWeight(newOrderItemChange)
    }

    return (
      <Fragment>
        {items.length > 1 && <div className={"card-material"}>
          <div className={"temp-title"} style={{ backgroundColor: '#fff' }}>
            <div className={"temp-heading-container"}>
              <div className={`temp-heading`}>Combine POs</div>
            </div>
            <div className={"temp-set"}>
              <Switch
                value={isShowCombinePOS}
                onValueChanged={(e) => setIsShowCombinePOS(e.value)}
              />
            </div>
          </div>
        </div>}
        {!isShowCombinePOS || items.length === 1 ? (
            <POCardLists
              stopId={stopId}
              lineItemId={lineItemId}
              onClickOperationMode={onClickOperationMode}
              onAddCatchWeight={onAddCatchWeight}
            />
        ) : (
          <>
            <CatchWeightQuantityCard stopId={stopId} lineItemId={lineItemId} />
            <div className={'card-material'}>
              <CatchWeightCard
                stopId={stopId}
                lineItemId={lineItemId}
                onClickOperationMode={onClickOperationMode}
                onAddCatchWeight={onAddCatchWeight}
                onClickShowVerifiedButton={() => setIsShowVerifiedItemsEnabled(!isShowVerifiedItemsEnabled)}
                isShowVerifiedItemsEnabled={isShowVerifiedItemsEnabled}
              />
            </div>
          </>
        )}
        <ConfirmationPopup
            isVisible={isUndoClicked}
            title="Confirm Undo"
            message={`Are you sure you want to undo this ?`}
            button1={{ icon: faTimes }}
            button2={{ icon: faThumbsUp }}
            onClickButton1={() => setIsUndoClicked(false)}
            onClickButton2={undoOperation}
        />
        <ConfirmationPopup
            isVisible={isVerifyAllClicked}
            title="Confirm Verify All"
            message={`Are you sure you want to verify all the remaining items?`}
            button1={{ icon: faTimes }}
            button2={{ icon: faThumbsUp }}
            onClickButton1={() => setIsVerifyAllClicked(false)}
            onClickButton2={() => updateCatchWeights(CatchWeightOptions.Verified)}
        />
        <ConfirmationPopup
            isVisible={isDeleteAllClicked}
            title="Confirm Delete All"
            message={`Are you sure you want to delete all the remaining catch weights for this item?`}
            render={ () =>
                <div className='toggle-confirm'>
                    <Switch value={isToggleClicked} onValueChanged={(e) => setToggleClicked(e.value) }/>
                    <div style={{paddingLeft:'1em'}}>Include verified catch weights for this item</div>
                </div>
            }
            button1={{ icon: faTimes }}
            button2={{ icon: faThumbsUp }}
            onClickButton1={() => setIsDeleteAllClicked(false)}
            onClickButton2={() => {updateCatchWeights(CatchWeightOptions.Delete)}}
        />
        <SecondaryPopup
            isVisible={isSecondaryPopupAddVisible}
            isReturnStickerAvailable={isReturnStickerAvailable}
            heading={'Add Catch Weight'}
            secondaryPopUpValue={reconcileCatchWeight}
            onClickOk={onClickSecondaryPopup}
            onClickCancel={() => completeReconcilingCatchWeight(CatchWeightOptions.Add)}
            onValueChanged={onValueChanged}
            reconcileReasons={reconcileReasons.filter(reason => reason.description.startsWith(ReasonClass.Overage))}
            refusalReasons={refusalReasons}>
        </SecondaryPopup>
        <SecondaryPopup
            isVisible={isSecondaryPopupDeleteVisible}
            isReturnStickerAvailable={isReturnStickerAvailable}
            heading={'Remove Catch Weight'}
            secondaryPopUpValue={reconcileCatchWeight}
            onClickOk={onClickSecondaryPopup}
            onClickCancel={() => completeReconcilingCatchWeight(CatchWeightOptions.Delete)}
            onValueChanged={onValueChanged}
            reconcileReasons={reconcileReasons.filter(reason => reason.class != ReasonClass.Overage && reason.class != ReasonClass.Even)}
            refusalReasons={refusalReasons}>
        </SecondaryPopup>
        <div className={'bottom-fix-action-row'}>
            <ActionButton
                icon={faTimes}
                buttonColor={'#F2994A'}
                buttonText={deleteButtonText}
                onClick={() => setIsDeleteAllClicked(true)}
            />
            <ActionButton
                icon={faTasks}
                buttonColor={'#EB5757'}
                buttonText={verifyButtonText}
                onClick={() => setIsVerifyAllClicked(true)}
            />
        </div>
      </Fragment>
    )
}
